import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Modal, type MenuProps } from 'antd';
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { useDeleteAdditionalCost } from 'api/additional-cost/use-delete-additional-cost';
import { useUpdateAdditionalCost } from 'api/additional-cost/use-update-additional-cost';
import type { ECurrency } from 'types/currencies';
import type { TDropdownChangeInfo } from 'types/dropdown-change-info';
import { AdditionalCostModal } from 'components/common/additional-cost-modal';
import type { IAdditionalCostFormFields } from 'components/common/additional-cost-modal/types';
import type { IAdditionalCostControlProps } from './types';

export const AdditionalCostControl = (props: IAdditionalCostControlProps) => {
  const { orderId, tripId, additionalCost } = props;
  const { t } = useTranslation();
  const [modal, contextHolder] = Modal.useModal();

  const [isActiveControl, setIsActiveControl] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const { mutateAsync: deleteAdditionalCost } = useDeleteAdditionalCost({ orderId, tripId });
  const { mutateAsync: updateAdditionalCost, isLoading: isUpdateLoading } = useUpdateAdditionalCost(additionalCost.id, {
    orderId,
    tripId,
  });

  const onFinishUpdate = async (values: IAdditionalCostFormFields) => {
    await updateAdditionalCost(values);
  };

  const deactivateWithTimeout = () => {
    setTimeout(() => setIsActiveControl(false), 300);
  };

  const confirm = () => {
    modal.confirm({
      title: t('common:confirm'),
      icon: <ExclamationCircleOutlined />,
      content: t('common:delete_confirm_text'),
      okText: t('common:delete'),
      cancelText: t('common:cancel'),
      onCancel: () => deactivateWithTimeout(),
      onOk: async () => {
        await deleteAdditionalCost(additionalCost.id);
        deactivateWithTimeout();
      },
    });
  };

  const handleDropdownClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'delete') {
      confirm();
    } else if (key === 'edit') {
      setIsEditOpen(true);
    }
  };

  const handleDropdownOpenChange = (value: boolean, info: TDropdownChangeInfo) => {
    if (info.source === 'trigger') {
      setIsActiveControl(value);
    }
  };

  const initialValues: IAdditionalCostFormFields = {
    type: additionalCost.type,
    value: additionalCost.value,
    currency: additionalCost.currency as ECurrency,
  };

  return (
    <>
      <Dropdown
        key='more'
        trigger={['click']}
        menu={{
          items: [
            {
              key: 'edit',
              label: t('common:edit'),
              icon: <EditOutlined />,
            },
            {
              key: 'delete',
              label: t('common:delete'),
              icon: <DeleteOutlined />,
            },
          ],
          onClick: handleDropdownClick,
        }}
        placement='bottomLeft'
        onOpenChange={handleDropdownOpenChange}>
        <MoreOutlined style={{ fontSize: 20 }} />
      </Dropdown>
      {isActiveControl ? contextHolder : null}
      {isEditOpen ? (
        <AdditionalCostModal
          isOpen={isEditOpen}
          isLoading={isUpdateLoading}
          initialValues={initialValues}
          onFinish={onFinishUpdate}
          onClose={() => setIsEditOpen(false)}
        />
      ) : null}
    </>
  );
};
